/**
 * Pure presentation component for the contract upload section.
 *
 * Renders the dynamic title, the player-name description, the
 * FileUploadZone, the staged-files list, the privacy disclaimer, the
 * inline error region, and the Extract / Cancel action buttons. The
 * parent container owns all state — staged files, upload progress,
 * error message — and forwards them via props.
 * @module features/contracts/components/ContractUploadSection/ContractUploadSectionView
 */

import { Info } from "lucide-react-native";
import React, { memo } from "react";

import type {
  ContractDocumentType,
  StagedContractFile,
} from "@/features/contracts/types";
import ContractFileListItem from "@/features/contracts/components/molecules/ContractFileListItem";
import FileUploadZoneContainer from "@/features/contracts/components/molecules/FileUploadZone/FileUploadZoneContainer";
import PrivacyDisclaimerView from "@/features/contracts/components/molecules/PrivacyDisclaimer/PrivacyDisclaimerView";

import { Alert, AlertIcon, AlertText } from "@/components/atoms/Alert";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

/** Props for {@link ContractUploadSectionView}. */
interface Props {
  readonly title: string;
  /**
   * Active document type. Drives the body copy and the drop-zone
   * heading so they match the already-dynamic section title. The View
   * stays a pure presentation component — the Container resolves the
   * active type from the drawer store and forwards it.
   */
  readonly documentType: ContractDocumentType;
  /** Whether the Extract button is enabled — files staged and none in flight. */
  readonly canExtract: boolean;
  /**
   * The noun for the body copy, mirroring the section header ("contract files"
   * vs "transfer agreement files"). Two phrasings do not justify an i18n key
   * map, so the Container picks between them.
   */
  readonly bodyAssetLabel: string;
  readonly playerName: string;
  readonly stagedFiles: readonly StagedContractFile[];
  readonly isUploading: boolean;
  readonly errorMessage: string | null;
  readonly hasExtraction: boolean;
  readonly onFilesSelected: (files: readonly StagedContractFile[]) => void;
  readonly onInvalidFiles: (
    rejections: readonly { readonly name: string; readonly reason: string }[]
  ) => void;
  readonly onRemoveFile: (id: string) => void;
  readonly onExtract: () => void;
  readonly onCancel: () => void;
}

const SECTION_TEST_ID = "contract-upload-section";
const ERROR_TEST_ID = "contract-upload-error";
const EXTRACT_BUTTON_TEST_ID = "contract-upload-extract";
const CANCEL_BUTTON_TEST_ID = "contract-upload-cancel";

/**
 * Renders the contract upload section UI.
 * @param props - View props
 * @param props.title - Dynamic header (Upload Contract Documents / Upload Transfer Agreement)
 * @param props.documentType - Active document type; drives body + drop-zone subcopy
 * @param props.playerName - Display name of the active player
 * @param props.stagedFiles - Files the user has picked but not yet uploaded
 * @param props.isUploading - Whether an upload is currently in flight
 * @param props.errorMessage - Inline error to render below the file list, or null
 * @param props.hasExtraction - Whether extraction data already exists for this player
 * @param props.onFilesSelected - Invoked when valid files are picked or dropped
 * @param props.onInvalidFiles - Invoked when picked files fail validation
 * @param props.onRemoveFile - Invoked with a file id when the remove button is pressed
 * @param props.onExtract - Invoked when the user taps "Extract Key Terms"
 * @param props.canExtract - Whether the Extract button is enabled
 * @param props.bodyAssetLabel - The noun for the body copy
 * @param props.onCancel - Invoked when the user taps "Cancel"
 * @returns The rendered upload section
 */
const ContractUploadSectionView = ({
  title,
  documentType,
  playerName,
  stagedFiles,
  isUploading,
  errorMessage,
  hasExtraction,
  onFilesSelected,
  onInvalidFiles,
  onRemoveFile,
  onExtract,
  canExtract,
  bodyAssetLabel,
  onCancel,
}: Props) => (
  <Box testID={SECTION_TEST_ID} className="flex-1">
    <Stack className="flex-1 gap-4 p-4">
      <Stack className="gap-1">
        <Heading variant="title" className="text-content-primary">
          {title}
        </Heading>
        <Text variant="body" className="text-content-secondary">
          {`Upload ${bodyAssetLabel} for ${playerName}. Supported formats: PDF, JPEG, PNG.`}
        </Text>
      </Stack>

      {hasExtraction ? (
        <Alert
          action="info"
          testID="upload-blocked-message"
          data-testid="upload-blocked-message"
        >
          <AlertIcon as={Info} />
          <AlertText>
            This player already has extracted contract data. To upload new
            documents, delete the existing data first.
          </AlertText>
        </Alert>
      ) : (
        <>
          <FileUploadZoneContainer
            disabled={isUploading}
            documentType={documentType}
            onFilesSelected={onFilesSelected}
            onInvalidFiles={onInvalidFiles}
          />

          {stagedFiles.length > 0 ? (
            <Box className="rounded-none border border-surface-muted">
              {stagedFiles.map(file => (
                <ContractFileListItem
                  key={file.id}
                  id={file.id}
                  fileName={file.name}
                  fileSize={file.size}
                  mimeType={file.type}
                  disabled={isUploading}
                  onRemove={onRemoveFile}
                />
              ))}
            </Box>
          ) : null}

          {errorMessage ? (
            <Box testID={ERROR_TEST_ID}>
              <Box className="rounded-none border border-needs-semantic-error-200 bg-needs-semantic-error-50 p-3">
                <Text variant="body" className="text-needs-semantic-error-700">
                  {errorMessage}
                </Text>
              </Box>
            </Box>
          ) : null}

          <PrivacyDisclaimerView />

          <Stack
            direction="horizontal"
            className="items-center justify-end gap-2 pt-2"
          >
            {isUploading ? (
              <Stack
                direction="horizontal"
                className="mr-auto items-center gap-2"
              >
                <Spinner size="small" />
                <Text variant="body" className="text-content-secondary">
                  {`Uploading ${stagedFiles.length} file${
                    stagedFiles.length === 1 ? "" : "s"
                  }…`}
                </Text>
              </Stack>
            ) : null}
            <Button
              testID={CANCEL_BUTTON_TEST_ID}
              onPress={onCancel}
              tone="secondary"
              variant="outline"
              isDisabled={isUploading}
              label="Cancel"
            />
            <Button
              testID={EXTRACT_BUTTON_TEST_ID}
              onPress={onExtract}
              tone="primary"
              isDisabled={!canExtract}
              label="Extract Key Terms"
            />
          </Stack>
        </>
      )}
    </Stack>
  </Box>
);

ContractUploadSectionView.displayName = "ContractUploadSectionView";

export default memo(ContractUploadSectionView);
